import type {Evaluator} from "@cm/graph-v3"
import {registerTemplateGraphNode} from "#template/template-node/register"
import {ObjectNode} from "#template/nodes/object/object-node"
import {z} from "zod/v4-mini"
import type {ObjectNodeDefinition, ObjectNodeParameters} from "#template/nodes/object/types"
import type {OnCompileContext, TemplateNodeContext, VisitorNodeResult} from "#template/template-node/types"
import {getTemplateNodeReturns} from "#template/template-node/validation"
import {objectNodeParameters} from "#template/nodes/object/validation"
import {objectLike} from "#template/nodes/object/fundamental"
import type {ObjectLikeNode} from "#template/nodes/types"
import type {RawBounds} from "#template/geometry-processing/bounds"
import type {BuilderOutlet} from "#template/runtime-graph/graph-builder"
import type {SceneNodes} from "#template/interfaces/scene-object"
import {visitNone} from "#template/template-node/visit-helpers"

export const DimensionGuideBoxTypename = "DimensionGuideBox"

export type DimensionGuideBoxParameters = {
    name: string
    width: number
    height: number
    depth: number
    parent: ObjectLikeNode | null
} & ObjectNodeParameters

export type DimensionGuideBoxConstructorParameters = Omit<DimensionGuideBoxParameters, "parent" | "visible"> & {
    parent?: ObjectLikeNode | null
    visible?: boolean
}

const dimensionGuideBoxDefinition: ObjectNodeDefinition<DimensionGuideBoxParameters, DimensionGuideBoxConstructorParameters> = {
    nodeClass: "DimensionGuideBox",
    validation: {
        parameters: z.extend(
            z.object({
                name: z.string(),
                width: z.number(),
                height: z.number(),
                depth: z.number(),
                parent: z._default(z.nullable(objectLike), null),
            }),
            objectNodeParameters.shape,
        ),
        returns: getTemplateNodeReturns(),
    },
}

@registerTemplateGraphNode
export class DimensionGuideBox extends ObjectNode<DimensionGuideBoxParameters, DimensionGuideBoxConstructorParameters> {
    public override get definition() {
        return dimensionGuideBoxDefinition
    }

    public override onCompile(context: OnCompileContext, _evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<DimensionGuideBoxParameters> {
        const {evaluator} = context
        const {width, height, depth, parent} = this.parameters

        const scope = evaluator.getScope(this)

        const bounds: BuilderOutlet<RawBounds> = scope.pureLambda(
            scope.tuple(width, height, depth),
            ([w, h, d]) => {
                const halfW = w / 2,
                    halfH = h / 2,
                    halfD = d / 2
                return {
                    centroid: [0, 0, 0],
                    surfaceArea: 2 * (w * h + w * d + h * d),
                    aabb: [
                        [-halfW, -halfH, -halfD],
                        [halfW, halfH, halfD],
                    ],
                    radii: {
                        xy: Math.sqrt(halfW * halfW + halfH * halfH),
                        xz: Math.sqrt(halfW * halfW + halfD * halfD),
                        yz: Math.sqrt(halfH * halfH + halfD * halfD),
                        xyz: Math.sqrt(halfW * halfW + halfH * halfH + halfD * halfD),
                    },
                }
            },
            "DimensionGuideBoxBounds",
        )

        this.setupObject(
            scope,
            context,
            DimensionGuideBoxTypename,
            bounds,
            evaluator.evaluate(parent),
            (objectProps) => {
                return scope.struct<SceneNodes.Cuboid>("Cuboid", {
                    type: "Cuboid",
                    ...objectProps,
                    width,
                    height,
                    depth,
                })
            },
            undefined,
        )

        return visitNone(this.parameters)
    }
}
